import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Message,
  Segment
} from 'semantic-ui-react';
import * as microsoftTeams from '@microsoft/teams-js';
import { CONFIG } from '../../helper/config';
import { fetchUserLocation } from '../../actions/card-library.actions';
import LoaderComponent from '../loader/loader';

interface IProps {
  is_license_expire: boolean;
  unregisterd_user_msg: string;
  is_unregisterd_user: boolean;
  fetchUserLocation: (info: any, token: any,ms?: any)=> void;
}

interface IState {
  unique_id: string;
  teams_context: any;
  open_modal: boolean;
  port_no: string;
}

/**
 * FetchUserLocation component
 *
 * Handles retrieving user and context information in both
 * Microsoft Teams and WebApp environments, including geolocation data,
 * and dispatches this information to the backend.
 *
 * @remarks
 * - In Teams: initializes Teams SDK, retrieves context and auth token,
 *   and fetches the user's current geolocation.
 * - In WebApp: listens for `message` events from the parent window,
 *   extracts context + location, and dispatches them.
 */
export class FetchUserLocation extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      unique_id: '',
      teams_context: {},
      open_modal: false,
      port_no: '',
    };
  }


  /**
   * Lifecycle method: Called once when the component mounts.
   *
   * Detects whether the environment is **Microsoft Teams** or **WebApp**
   * and executes the relevant logic:
   *
   * - **Teams**:
   *   - Initializes Teams app and notifies success.
   *   - Retrieves Teams context (user, tenant, channel, etc.).
   *   - Obtains auth token.
   *   - Fetches browser geolocation and dispatches it with context.
   *
   * - **WebApp**:
   *   - Listens for `message` events from parent.
   *   - Validates event origin.
   *   - Extracts auth token, user info, and location from event payload.
   *   - Dispatches via {@link fetchUserLocation}.
   */
  componentDidMount() {
    const query_params = window.location.search.split("?")[1];
    let isWebApp = query_params
      ? query_params.split(CONFIG.URLQUERYPARAMSEPERATOR)[5]
      : "";
    const port_no: any = query_params
      ? query_params.split(CONFIG.URLQUERYPARAMSEPERATOR)[0]
      : "";
    const file_name: any = query_params
      ? query_params.split(CONFIG.URLQUERYPARAMSEPERATOR)[1]
      : "";
    if (
      query_params &&
      query_params.split(CONFIG.URLQUERYPARAMSEPERATOR)[6] === CONFIG.WEBAPP &&
      isWebApp === "notify_me"
    ) {
      isWebApp = CONFIG.WEBAPP;
    }
    if (isWebApp !== CONFIG.WEBAPP) {
      microsoftTeams.app.initialize();
      microsoftTeams.app.notifySuccess();
      let team_context: any = {};
      microsoftTeams.app.getContext().then((context) => {

        team_context.user_id = context.user?.id;
        team_context.user_name = context.user?.userPrincipalName;
        team_context.tenant_id = context.user?.tenant?.id;
        team_context.file_name = file_name;
        team_context.teams_channel_id = context.channel?.id;
        team_context.channelId = "msteams";

        microsoftTeams.authentication.getAuthToken().then((token: string) => {
            let info = {
              va_id: query_params
                ? query_params.split(CONFIG.URLQUERYPARAMSEPERATOR)[2]
                : "",
              platform: "teams",
              port_no: port_no,
              ...team_context,
            };
            navigator.geolocation.getCurrentPosition(async(position) => {
                let updated_info = {
                  ...info,
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
              }
                this.props.fetchUserLocation(updated_info, token, microsoftTeams)
            })
        }).catch((error) => {
          console.error("error :: ", error);
        });
      }).catch((err) => console.error("Failed to get context:", err));

    } else {
      let webAppData;
        /**
         * Handles the message event from the parent window.
         * - Checks if the event origin matches the parentUrl.
         * - Parses the event data and extracts the necessary information.
         * - Calls the fetchUserLocation action.
         * - Sets the component state.
         * @param {object} event - The event object containing the data sent from the parent window.
         */
      const handleMessage = (event) => {
        if (event.origin !== event?.data?.parentUrl) return;
        webAppData = { ...event.data };
        const token = webAppData.token;
        const info = {
          va_id: query_params
            ? query_params.split(CONFIG.URLQUERYPARAMSEPERATOR)[2]
            : "",
          port_no: port_no,
          user_id: webAppData.aad_object_id,
          user_name: webAppData.email,
          tenant_id: webAppData.tenantId,
          file_name: file_name,
          channelId: "msteams",
          platform: "webapp",
          webAppUrl: webAppData.parentUrl,
          latitude:webAppData.latitude,
          longitude: webAppData.longitude,
        };
        this.props.fetchUserLocation(info, token)
        this.setState({
          port_no: port_no,
          teams_context:info,
        });
      };
      window.addEventListener('message', handleMessage);
    }
  }


  /**
   * Renders the component based on the user's registration and license status.
   * 
   * - If the user is unregistered, displays a message defined in `unregisterd_user_msg`.
   * - If the license has expired, displays a license expiration message.
   * - Otherwise, renders a loader for screens wider than the mobile width threshold, 
   *   or a download message for smaller screens.
   */

  render() {
    let {   is_license_expire, is_unregisterd_user, unregisterd_user_msg } = this.props;
    let screen_width=window.screen.width;
    if (is_unregisterd_user) {
      return <Segment dangerouslySetInnerHTML={{ __html: unregisterd_user_msg }}
        placeholder textAlign="center" size="large" />
    }
    else if (is_license_expire) {
      return <Segment placeholder textAlign="center" size="large" content={CONFIG.LICENSE_EXPIRE} />
    } else {
      return (
        screen_width > CONFIG.PVP.MOBILEWIDTH ? 
       <LoaderComponent active_loader={true} loading_time={0}/>
       : <Message floating>{CONFIG.CSVDOWNLOADMESSAGE}</Message>
      );
    }
  }
}

/**
 * Maps the relevant portions of the application state to props for the component.
 *
 * Retrieves the license expiration status and active loader state from the view state.
 * 
 * @param {Object} state - The state of the application.
 * @returns {Object} A props object containing the license expiration status and active loader state.
 */

const mapStateToProps = (state: any) => {
  return {
    is_license_expire: state.viewState.is_license_expire,
    active_loader: state.viewState.active_loader,
  };
};

export default connect(mapStateToProps, { fetchUserLocation, 
})(FetchUserLocation);
